import Button from "@mui/material/Button";
import Modal from "../shared/Modal/Modal";
import { Form, Formik } from "formik";
import { useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import { errorMessages } from "../../constants/errorMessages";
import { regex } from "../../constants/regex";
import Input from "../shared/Input/Input";
import "./ChangePassword.scss";
import { ReactComponent as ChangePasswordIcon } from "../../assets/svg/ChangePassword.svg";
import { useLazyChangePasswordQuery } from "../../services/rtkQueryServices/authenticationService";

export default function ChangePassword(props: any) {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [changePassword] = useLazyChangePasswordQuery();
  const initialFormValue = useMemo<any>(() => {
    return {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    };
  }, []);
  const formRef = useRef<any>(null);
  const validationSchema = () =>
    Yup.object({
      currentPassword: Yup.string().required(
        errorMessages.required("Current password")
      ),
      newPassword: Yup.string()
        .required(errorMessages.required("New password"))
        .min(8, errorMessages.minCharacterLimit("New password", 8))
        .max(64, errorMessages.characterLimit("New password", 64))
        .matches(regex.passwordValidation, errorMessages.passwordRegexMatch)
        .notOneOf([Yup.ref("currentPassword")], errorMessages.passwordMismatch),
      confirmNewPassword: Yup.string()
        .required(errorMessages.renterPassword)
        .oneOf([Yup.ref("newPassword")], errorMessages.confirmPassword),
    });
  const handleOnChange = () => {
    setErrorMessage("");
  };

  return (
    <>
      <Modal
        title="Change Password"
        headerLogo={<ChangePasswordIcon />}
        modalHeaderStyle={{ fontSize: "2rem" }}
        modalWidth="480px"
        isOpen={props.isOpen}
        children={
          <div>
            <Formik
              innerRef={formRef}
              initialValues={initialFormValue}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                changePassword(values)
                  .then((res: any) => {
                    if (res?.data?.isSuccess && res?.data?.data) {
                      setErrorMessage("");
                      props?.onClose();
                    } else if (res?.isError && res?.error?.status !== 500) {
                      setErrorMessage(
                        res?.error?.data?.message ||
                          errorMessages.passwordUpdateFailed
                      );
                    } else {
                      setErrorMessage(errorMessages.passwordUpdateFailed);
                    }
                  })
                  .catch(() => {
                    setErrorMessage(errorMessages.passwordUpdateFailed);
                  });
              }}
            >
              {(formik) => (
                <Form
                  onSubmit={formik.handleSubmit}
                  className="change-password-form-container"
                >
                  <div className="change-password-form d-flex flex-column p-5">
                    <Input
                      name="currentPassword"
                      label="Current Password"
                      placeholder="Enter current password"
                      type="password"
                      errorMsg={
                        formik.touched.currentPassword &&
                        formik.errors.currentPassword &&
                        (formik.errors as any).currentPassword
                      }
                      value={formik.values.currentPassword}
                      onInput={formik.handleChange}
                      onChange={handleOnChange}
                      required
                    />
                    <Input
                      name="newPassword"
                      label="New Password"
                      placeholder="Enter new password"
                      type="password"
                      labelHelpText={`${
                        errorMessages.passwordRegexMatch
                      }\n${errorMessages.previousPasswords()}`}
                      infoIconProps={{ width: "14px", height: "14px" }}
                      errorMsg={
                        formik.touched.newPassword &&
                        formik.errors.newPassword &&
                        (formik.errors as any).newPassword
                      }
                      value={formik.values.newPassword}
                      onInput={formik.handleChange}
                      onChange={handleOnChange}
                      required
                    />
                    <Input
                      name="confirmNewPassword"
                      label="Confirm New Password"
                      placeholder="Confirm new password"
                      type="password"
                      errorMsg={
                        formik.touched.confirmNewPassword &&
                        formik.errors.confirmNewPassword &&
                        (formik.errors as any).confirmNewPassword
                      }
                      required
                      value={formik.values.confirmNewPassword}
                      onInput={formik.handleChange}
                      onChange={handleOnChange}
                    />
                  </div>
                  {errorMessage && (
                    <div className="error-message px-5">{errorMessage}</div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        }
        onClose={() => {
          setErrorMessage("");
          props.onClose();
        }}
        closeOnOverLayClick={false}
        headerBg="var(--primary-color)"
        closeIconColor={"var(--white)"}
        modalFooter={
          <div className="d-flex justify-content-between change-password-footer gap-1 px-5 py-3">
            <Button
              className="cancel-button px-5"
              onClick={() => {
                setErrorMessage("");
                props.onClose();
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              className="update-button px-5"
              variant="contained"
              onClick={() => {
                formRef.current.handleSubmit();
              }}
            >
              Update Password
            </Button>
          </div>
        }
      />
    </>
  );
}
